import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetCurrentAnnouncements } from '@bonusly/common-announcements';
import { Badge, HStack, MegaphoneOutline, Text } from '@bonusly/design-system';
import { useDispatch } from '@bonusly/events';

import { useAnnouncementDismissals } from '../../../components/announcements/hooks';
import { AnnouncementActionEvent } from '../../../components/announcements/types/events';
import { Reveal } from '../../../components/display/reveal';
import { useCloseNavbarPanels } from '../../../hooks/use-navbar-panels';
import { AnalyticsEvent } from '../../../lib/analytics/events';
import { useAnalytics } from '../../../lib/analytics/use-analytics';
import { UserNavAction } from './user-nav-action';

interface AnnouncementMenuItemProps {
	isExpanded: boolean;
	onClose?: () => void;
}

export const AnnouncementMenuItem: FC<AnnouncementMenuItemProps> = ({ isExpanded, onClose }) => {
	const { t } = useTranslation('navigation');
	const { track } = useAnalytics();
	const { data: announcements } = useGetCurrentAnnouncements();
	const { isAnnouncementDismissed } = useAnnouncementDismissals();
	const dispatch = useDispatch();
	const closeAllPanels = useCloseNavbarPanels();
	const hasActiveAnnouncements = announcements && announcements.length > 0;

	const unreadCount = useMemo(() => {
		if (!announcements) return 0;
		return announcements.filter((announcement) => !isAnnouncementDismissed(announcement.id)).length;
	}, [announcements, isAnnouncementDismissed]);

	if (!hasActiveAnnouncements) {
		return null;
	}

	const handleClick = () => {
		track(AnalyticsEvent.UserNavbar.AnnouncementsNavigation);
		const firstAnnouncementId = announcements?.[0]?.id;
		if (firstAnnouncementId) {
			// NOTE: open to first announcement so it can be marked as read
			dispatch(AnnouncementActionEvent.OpenAnnouncement, { id: firstAnnouncementId });
		}
		closeAllPanels();
		onClose?.();
	};

	return (
		<UserNavAction onClick={handleClick} label={t('links.announcements')} isExpanded={isExpanded} data-testid="nav-link-announcements" mb={{ base: 0, sm: 3 }}>
			<HStack justifyContent="space-between" alignItems="center" spacing={3} w="full">
				<MegaphoneOutline size="md" />
				<HStack
					justifyContent="space-between"
					alignItems="center"
					flex={1}
					minW={0}
					opacity={isExpanded ? 1 : 0}
					transition={isExpanded ? 'opacity 0.15s 0.075s linear' : 'opacity 0.15s cubic-bezier(.4, 0, .2, 1)'}
				>
					<Text fontSize={100} fontWeight="medium" whiteSpace="nowrap">
						{t('links.announcements')}
					</Text>
					<Reveal if={unreadCount > 0}>
						<Badge
							alignItems="center"
							bg="primary.bgAlpha"
							borderRadius="full"
							color="primary.fg"
							display="inline-flex"
							fontSize="xs"
							fontWeight="semibold"
							justifyContent="center"
							px={2}
							py={0.5}
							whiteSpace="nowrap"
						>
							{unreadCount}
						</Badge>
					</Reveal>
				</HStack>
			</HStack>
		</UserNavAction>
	);
};
